import { Group, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';
import { useLanguage } from '@contexts/LanguageContext';
import { useTextScale, type TextScale } from '@contexts/TextScaleContext';
import { colors as palettes, SELECTABLE_THEME_MODES, type ThemeMode } from '@constants/colors';
import { LANGUAGES, type LanguageCode } from '@constants/translations';

/** Same names the sidebar footer uses for the theme toggle. */
const THEME_LABEL_KEY: Partial<Record<ThemeMode, string>> = {
  jarvis: 'theme.jarvis',
  'jarvis-night': 'theme.jarvisNight',
  horizon: 'theme.horizon',
  'horizon-night': 'theme.horizonNight',
  dawn: 'theme.horizonDawn',
  dusk: 'theme.horizonDusk',
  mist: 'theme.horizonMist',
};

const TEXT_SCALE_OPTIONS: { value: TextScale; labelKey: string; pct: string }[] = [
  { value: 1, labelKey: 'settings.textSizeNormal', pct: '100%' },
  { value: 1.1, labelKey: 'settings.textSizeLarge', pct: '110%' },
  { value: 1.2, labelKey: 'settings.textSizeLarger', pct: '120%' },
];

export interface TampilanControlsProps {
  /** Renders the language control on its own row under the swatches (narrow hosts). */
  stacked?: boolean;
  /**
   * z-index for the language dropdown. Mantine portals it at 300, which is
   * under a full-screen modal; a host inside one passes its own z plus one.
   */
  dropdownZIndex?: number;
}

/**
 * Theme, language and text size, the three personal display settings.
 *
 * One component for the two places that offer them, the Pengaturan page and
 * the first-login wizard, so the controls and their copy cannot drift.
 */
export default function TampilanControls({ stacked = false, dropdownZIndex }: TampilanControlsProps) {
  const { colors: themeColors, mode: themeMode, toggleTheme } = useTheme();
  const { t, language, setLanguage } = useLanguage();
  const { scale: textScale, setScale: setTextScale } = useTextScale();

  const themeSwatches = (
    <Stack gap="xs">
      <Text fw={600} style={{ color: themeColors.text }}>
        {t('settings.theme')}
      </Text>
      <Text size="sm" style={{ color: themeColors.textSecondary }}>
        {t('settings.themeDesc')}
      </Text>
      <Group gap="sm" wrap="wrap" mt={4}>
        {SELECTABLE_THEME_MODES.map((option) => {
          const palette = (palettes as Record<string, Record<string, string | undefined>>)[option] ?? {};
          const selected = option === themeMode;
          return (
            <button
              key={option}
              type="button"
              onClick={() => toggleTheme(option)}
              aria-pressed={selected}
              aria-label={t(THEME_LABEL_KEY[option] ?? 'sidebar.theme')}
              style={{
                width: 104,
                padding: 0,
                borderRadius: 8,
                overflow: 'hidden',
                cursor: 'pointer',
                background: themeColors.card,
                border: `1.5px solid ${selected ? themeColors.accent : themeColors.border}`,
                boxShadow: selected ? `0 0 0 2px ${themeColors.accent}33` : undefined,
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', height: 36 }}>
                <span style={{ backgroundColor: palette.sidebar ?? palette.background }} />
                <span style={{ backgroundColor: palette.accent }} />
                <span style={{ backgroundColor: palette.background }} />
              </div>
              <div
                style={{
                  padding: '4px 6px',
                  fontSize: 12,
                  color: selected ? themeColors.text : themeColors.textSecondary,
                  fontWeight: selected ? 600 : 500,
                  textAlign: 'center',
                }}
              >
                {t(THEME_LABEL_KEY[option] ?? 'sidebar.theme')}
              </div>
            </button>
          );
        })}
      </Group>
    </Stack>
  );

  const languageSelect = (
    <Select
      value={language}
      onChange={(value) => {
        if (value) setLanguage(value as LanguageCode);
      }}
      data={LANGUAGES.map((lang) => ({ value: lang.code, label: `${lang.flag} ${lang.label}` }))}
      allowDeselect={false}
      w={200}
      comboboxProps={{ withinPortal: true, zIndex: dropdownZIndex }}
      aria-label={t('settings.language')}
      styles={{
        input: {
          backgroundColor: themeColors.formFields,
          color: themeColors.text,
          borderColor: themeColors.border,
        },
      }}
    />
  );

  const languageRow = stacked ? (
    <Stack gap="xs">
      <Text fw={600} style={{ color: themeColors.text }}>
        {t('settings.language')}
      </Text>
      <Text size="sm" style={{ color: themeColors.textSecondary }}>
        {t('settings.languageDesc')}
      </Text>
      {languageSelect}
    </Stack>
  ) : (
    <Group justify="space-between" align="flex-start" wrap="nowrap">
      <div style={{ flex: 1 }}>
        <Text fw={600} style={{ color: themeColors.text }}>
          {t('settings.language')}
        </Text>
        <Text size="sm" style={{ color: themeColors.textSecondary }}>
          {t('settings.languageDesc')}
        </Text>
      </div>
      {languageSelect}
    </Group>
  );

  const textSize = (
    <Stack gap="xs">
      <Text fw={600} style={{ color: themeColors.text }}>
        {t('settings.textSize')}
      </Text>
      <Text size="sm" style={{ color: themeColors.textSecondary }}>
        {t('settings.textSizeDesc')}
      </Text>
      <SegmentedControl
        value={String(textScale)}
        onChange={(value) => {
          const next = Number(value);
          if (next === 1 || next === 1.1 || next === 1.2) setTextScale(next);
        }}
        data={TEXT_SCALE_OPTIONS.map((option) => ({
          value: String(option.value),
          label: `${t(option.labelKey)} (${option.pct})`,
        }))}
        color={themeColors.accent}
        aria-label={t('settings.textSize')}
      />
      <div
        style={{
          border: `1px dashed ${themeColors.border}`,
          borderRadius: 8,
          padding: '10px 12px',
          color: themeColors.textSecondary,
        }}
      >
        <Text size="sm" style={{ color: themeColors.text }}>
          {t('settings.textSizePreview')}
        </Text>
        <Text size="xs" style={{ color: themeColors.textSecondary }}>
          {t('settings.textSizeNote')}
        </Text>
      </div>
    </Stack>
  );

  return (
    <Stack gap="lg">
      {themeSwatches}
      {languageRow}
      {textSize}
    </Stack>
  );
}
